import { Button, Col, Icon, Input, Tappable, Text, useTheme } from "@rally/ui-kit";
import { useState } from "react";

import { say } from "../localization/say.ts";
import { act } from "../net/rally.ts";
import { appDraftErrors } from "./project-model.ts";

type CreatedPlacedApp = {
  app: { id: string; slug: string; name: string; description: string };
  placement: { id: string };
};

export function ProjectAppEditor({
  project,
  onCancel,
  onCreated,
}: {
  project: string;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const theme = useTheme();
  const [name, setName] = useState("");
  const [repositoryName, setRepositoryName] = useState("");
  const [repositoryUrl, setRepositoryUrl] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState(false);
  const [submission] = useState(() => crypto.randomUUID());
  const errors = appDraftErrors(name);
  const sourceValid =
    /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/.test(repositoryName.trim()) &&
    (() => {
      try {
        return new URL(repositoryUrl.trim()).protocol === "https:";
      } catch {
        return false;
      }
    })();
  const create = async () => {
    setSubmitted(true);
    setFailure(false);
    if (errors.name || !sourceValid || submitting) return;
    setSubmitting(true);
    try {
      await act<CreatedPlacedApp>("app.createPlaced", {
        submission,
        name: name.trim(),
        source: { name: repositoryName.trim(), url: repositoryUrl.trim() },
        context: "project",
        contextId: project,
      });
      onCreated();
    } catch {
      setFailure(true);
      setSubmitting(false);
    }
  };
  const field = {
    minHeight: 44,
    borderWidth: 1,
    borderColor: theme.color["border.default"],
    borderRadius: theme.radius.md,
    paddingHorizontal: theme.space[3],
    color: theme.color["text.primary"],
  };
  return (
    <Col testID="project-app-editor" style={{ gap: theme.space[3] }}>
      <Col style={{ flexDirection: "row", alignItems: "center" }}>
        <Text typeName="bodySemibold" color="text.primary" style={{ flex: 1 }}>
          {say("project.createApp")}
        </Text>
        <Tappable
          accessibilityRole="button"
          accessibilityLabel={say("panel.close")}
          testID="project-app-cancel"
          disabled={submitting}
          onPress={onCancel}
          style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}
        >
          <Icon name="close" size={17} color="text.secondary" />
        </Tappable>
      </Col>
      <Text typeName="label" color="text.secondary">
        {say("apps.name")}
      </Text>
      <Col
        style={{
          minHeight: 46,
          flexDirection: "row",
          alignItems: "center",
          gap: theme.space[2],
          paddingHorizontal: theme.space[3],
          borderWidth: 1,
          borderColor: theme.color["border.default"],
          borderRadius: theme.radius.md,
        }}
      >
        <Icon name="box" size={17} color="text.tertiary" />
        <Input
          nativeID="project-app-name"
          value={name}
          onChangeText={setName}
          accessibilityLabel={say("apps.name")}
          editable={!submitting}
          {...({
            "aria-invalid": submitted && errors.name,
            "aria-describedby": submitted && errors.name ? "project-app-name-error" : undefined,
          } as object)}
          style={
            { flex: 1, minHeight: 44, color: theme.color["text.primary"], outlineStyle: "none" } as object
          }
        />
      </Col>
      {submitted && errors.name ? (
        <Text
          nativeID="project-app-name-error"
          accessibilityLiveRegion="polite"
          typeName="caption"
          color="danger"
        >
          {say("project.nameInvalid")}
        </Text>
      ) : null}
      <Text typeName="label" color="text.secondary">
        {say("apps.sourceName")}
      </Text>
      <Input
        value={repositoryName}
        onChangeText={setRepositoryName}
        accessibilityLabel={say("apps.sourceName")}
        editable={!submitting}
        style={field}
      />
      <Text typeName="label" color="text.secondary">
        {say("apps.sourceUrl")}
      </Text>
      <Input
        value={repositoryUrl}
        onChangeText={setRepositoryUrl}
        accessibilityLabel={say("apps.sourceUrl")}
        autoCapitalize="none"
        editable={!submitting}
        style={field}
      />
      {submitted && !sourceValid ? (
        <Text accessibilityLiveRegion="polite" typeName="caption" color="danger">
          {say("apps.sourceInvalid")}
        </Text>
      ) : null}
      {failure ? (
        <Text accessibilityLiveRegion="polite" typeName="caption" color="danger">
          {say("apps.createFailed")}
        </Text>
      ) : null}
      <Text typeName="caption" color="text.tertiary">
        {say("project.createPlacementNote")}
      </Text>
      <Button
        testID="project-app-submit"
        loading={submitting}
        accessibilityLabel={submitting ? say("project.creatingApp") : say("project.createApp")}
        onPress={() => void create()}
      >
        {say("project.createApp")}
      </Button>
    </Col>
  );
}
